'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import LandingFeatureShowcase from './LandingFeatureShowcase'
import LandingHeroMedia from './LandingHeroMedia'
import LandingDetails from './LandingDetails'
import LandingBrowseGallery from './LandingBrowseGallery'
import LandingSocialProof from './LandingSocialProof'
import LandingChatSecondary from './LandingChatSecondary'
import { landingFaqs } from './landingContent'
import { landingArtwork } from './landingArtwork'
import styles from './LandingRedesign.module.css'

function Arrow() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SplitHeading({ text }) {
  const [first, second] = text.split('|')
  return (
    <>
      {first}
      <br />
      <span>{second}</span>
    </>
  )
}

function SignupLink({ children, location }) {
  return (
    <Link
      href="/signup"
      className={styles.cta}
      data-landing-cta={location}
      data-landing-action="signup"
    >
      {children}
      <Arrow />
    </Link>
  )
}

function MoreFeatures({ chatFirst, t, artwork }) {
  return (
    <section
      className={styles.moreFeatures}
      aria-labelledby="more-features-heading"
      data-landing-section="features"
    >
      <div className={styles.moreHeading}>
        <h2 id="more-features-heading">
          {t(chatFirst ? 'landingV2_moreConnect' : 'landingV2_moreCreate')}
        </h2>
      </div>
      <div className={styles.featureGrid}>
        <Link
          href={chatFirst ? '/chat' : '/generate'}
          className={styles.featureTile}
          data-landing-cta={chatFirst ? 'characters' : 'styles'}
          data-landing-action={chatFirst ? 'discover' : 'feature_images'}
        >
          <div className={`${styles.tileVisual} ${styles.styleVisual}`}>
            <div>
              <Image
                {...(chatFirst ? artwork.galleryCurls : artwork.cafeBlonde)}
                fill
                sizes="(max-width: 700px) 48vw, 220px"
              />
              <span>{t('landingV2_photo')}</span>
            </div>
            <div>
              <Image
                {...artwork.tealAnime}
                fill
                sizes="(max-width: 700px) 48vw, 220px"
              />
              <span>{t('landingV2_anime')}</span>
            </div>
          </div>
          <h3>
            {t(chatFirst ? 'landingV2_chemistryTitle' : 'landingV2_lookTitle')}
          </h3>
          <p>
            {t(chatFirst ? 'landingV2_chemistryBody' : 'landingV2_lookBody')}
          </p>
        </Link>
        <Link
          href={chatFirst ? '/chat' : '/generate'}
          className={styles.featureTile}
          data-landing-cta={chatFirst ? 'chat-media' : 'video'}
          data-landing-action="feature_video"
        >
          <div className={`${styles.tileVisual} ${styles.videoVisual}`}>
            <div className={styles.videoFrame}>
              <Image
                {...artwork.cafeAnime}
                fill
                sizes="(max-width: 700px) 65vw, 270px"
              />
              <span className={styles.imageToVideo}>
                {chatFirst ? (
                  t('landingV2_fromStory')
                ) : (
                  <>
                    {t('landingV2_image')} <span aria-hidden="true">→</span>{' '}
                    {t('landingV2_video')}
                  </>
                )}
              </span>
            </div>
          </div>
          <h3>
            {t(
              chatFirst
                ? 'landingV2_sceneToYouTitle'
                : 'landingV2_makeMoveTitle',
            )}
          </h3>
          <p>
            {t(
              chatFirst ? 'landingV2_sceneToYouBody' : 'landingV2_makeMoveBody',
            )}
          </p>
        </Link>
        <Link
          href={chatFirst ? '/chat' : '/explore'}
          className={styles.featureTile}
          data-landing-cta={chatFirst ? 'voice' : 'explore'}
          data-landing-action={chatFirst ? 'feature_voice' : 'explore'}
        >
          {chatFirst ? (
            <div className={`${styles.tileVisual} ${styles.voiceVisual}`}>
              <div className={styles.voiceTilePortrait}>
                <Image
                  {...artwork.redheadAnime}
                  fill
                  sizes="(max-width: 700px) 65vw, 260px"
                />
              </div>
              <div className={styles.voiceTileCall} aria-hidden="true">
                <span className={styles.voiceCallIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="m8 3 2 5-3 2c1.4 3 3 4.6 6 6l2-3 5 2v4c0 1.1-.9 2-2 2C9.7 21 3 14.3 3 6c0-1.1.9-2 2-2l3-1Z"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className={styles.voiceCallLabel}>
                  {t('landingV2_voiceCall')}
                  <svg width="103" height="19" viewBox="0 0 103 19" fill="none">
                    <path
                      d="M2 8v3m7-6v9m7-12v15m7-10v5m7-7v9m7-5v1m7-8v15m7-11v7m7-10v13m7-8v3m7-6v9m7-12v15m7-10v5m7-7v9m7-6v3"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          ) : (
            <div className={`${styles.tileVisual} ${styles.inspirationVisual}`}>
              <div className={styles.inspirationPortrait}>
                <Image
                  {...artwork.galleryCurls}
                  fill
                  sizes="(max-width: 700px) 65vw, 280px"
                />
              </div>
              <span className={styles.cloneChip}>
                {t('landingV2_cloneIdea')} <span aria-hidden="true">↗</span>
              </span>
            </div>
          )}
          <h3>
            {t(
              chatFirst
                ? 'landingV2_hearVoiceTitle'
                : 'landingV2_nextIdeaTitle',
            )}
          </h3>
          <p>
            {t(
              chatFirst ? 'landingV2_hearVoiceBody' : 'landingV2_nextIdeaBody',
            )}
          </p>
        </Link>
      </div>
    </section>
  )
}

export default function LandingRedesign({
  focus = 'create',
  heroImage,
  heroSlides,
  exploreFeed,
  featureArtwork = landingArtwork,
  featureVideoSrc,
  showGettingStarted = true,
  contentRevision,
  experimentId = 'landing_message_v3',
  experimentVariant,
  includeChatSection = false,
  revisedCreate = false,
}) {
  const { t, i18n } = useTranslation()
  const chatFirst = focus === 'chat'
  const artwork = Object.fromEntries(
    Object.entries(featureArtwork).map(([key, { altKey, ...image }]) => [
      key,
      {
        ...image,
        alt: altKey ? t(altKey, { defaultValue: image.alt }) : image.alt,
      },
    ]),
  )
  return (
    <div
      className={styles.landing}
      data-landing-redesign
      data-landing-focus={chatFirst ? 'chat' : 'create'}
      data-landing-variant={
        experimentVariant ?? (chatFirst ? 'chat' : 'create')
      }
      data-landing-experiment={experimentId}
      data-landing-content-revision={contentRevision}
      data-landing-locale={i18n.resolvedLanguage || i18n.language || 'en'}
    >
      <a className={styles.skipLink} href="#landing-main">
        {t('landingV2_skip')}
      </a>
      <main id="landing-main">
        <section
          className={styles.hero}
          aria-labelledby="landing-heading"
          data-landing-section="hero"
        >
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <LandingSocialProof />
              <h1 id="landing-heading">
                <SplitHeading
                  text={t(
                    chatFirst
                      ? 'landingV2_heroChatTitle'
                      : 'landingV2_heroCreateTitle',
                  )}
                />
              </h1>
              <p>
                {t(
                  chatFirst
                    ? 'landingV2_heroChatBody'
                    : revisedCreate
                      ? 'landingV5_heroCreateBody'
                      : 'landingV2_heroCreateBody',
                )}
              </p>
              <SignupLink location="hero">
                {t(
                  chatFirst
                    ? 'landingV2_startChatting'
                    : 'landingV2_startCreating',
                )}
              </SignupLink>
            </div>
            <div className={styles.heroArtwork}>
              <LandingHeroMedia
                heroImage={heroImage}
                slides={heroSlides}
                signupLinkLabel={
                  revisedCreate ? t('landingV5_heroSignupAria') : undefined
                }
              />
              {chatFirst && (
                <div
                  className={styles.heroMessage}
                  aria-label={t('landingV2_heroMessageAria')}
                >
                  <p>{t('landingV2_heroMessage')}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <LandingBrowseGallery
          focus={focus}
          exploreFeed={exploreFeed}
          featuredGrid={revisedCreate}
        />
        <LandingDetails focus={focus} placement="intro" />
        <LandingFeatureShowcase
          key={chatFirst ? 'chat' : 'create'}
          focus={focus}
          artwork={artwork}
          videoSrc={featureVideoSrc}
        />
        <MoreFeatures chatFirst={chatFirst} t={t} artwork={artwork} />
        <LandingDetails
          focus={focus}
          showCompanions={!includeChatSection}
          showSteps={showGettingStarted}
        />
        {includeChatSection && <LandingChatSecondary />}

        <section
          className={styles.faq}
          aria-labelledby="faq-heading"
          data-landing-section="faq"
        >
          <h2 id="faq-heading">{t('landingV2_faqHeading')}</h2>
          <div className={styles.faqItems}>
            {landingFaqs.map(({ questionKey, answerKey }) => (
              <details key={questionKey} className={styles.faqItem}>
                <summary data-landing-action="faq_open">
                  {t(questionKey)}
                  <span aria-hidden="true">+</span>
                </summary>
                <p>{t(answerKey)}</p>
              </details>
            ))}
          </div>
          <div className={styles.guides}>
            <Link href="/articles/promptchan-tutorial">
              {t('landingV2_imageGuide')}
            </Link>
            <Link href="/articles/promptchan-video-tutorial">
              {t('landingV2_videoGuide')}
            </Link>
          </div>
        </section>

        <section
          className={styles.closing}
          aria-labelledby="closing-heading"
          data-landing-section="closing"
        >
          <div className={styles.closingArtwork}>
            <Image
              {...artwork.gardenRedhead}
              fill
              sizes="(max-width: 700px) 100vw, 550px"
            />
          </div>
          <div className={styles.closingCopy}>
            <h2 id="closing-heading">
              <SplitHeading
                text={t(
                  chatFirst
                    ? 'landingV2_closingChatTitle'
                    : 'landingV2_closingCreateTitle',
                )}
              />
            </h2>
            <SignupLink location="closing">
              {t(
                chatFirst
                  ? 'landingV2_startChatting'
                  : 'landingV2_startCreating',
              )}
            </SignupLink>
          </div>
        </section>
      </main>
    </div>
  )
}
